import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup, firestore } from './firebase.config';
import './Login.css';
import { collection, query, where, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import { Button } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import ReCAPTCHA from 'react-google-recaptcha';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

const Home = ({ updateAuthState }) => {
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const navigate = useNavigate();

  // Function to check if the user's profile exists by email
  const getUserProfile = async (email) => {
    const userProfileRef = collection(firestore, 'UserProfileMaster');
    const q = query(userProfileRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const userProfile = querySnapshot.docs[0].data();
      // If email is in env, treat as SuperAdmin
      const superAdminEmails = [
        process.env.REACT_APP_ADMIN_EMAIL1,
        process.env.REACT_APP_ADMIN_EMAIL2,
        process.env.REACT_APP_ADMIN_EMAIL3
      ].filter(Boolean);
      const isSuperAdminEnv = superAdminEmails.includes(email);
      if (isSuperAdminEnv) {
        return {
          ...userProfile,
          role: 'SuperAdmin',
          isSuperAdmin: true,
          isAppAdmin: false,
          isAppUser: false
        };
      }
      return {
        ...userProfile,
        isSuperAdmin: false,
        isAppAdmin: userProfile.role === 'AppAdmin',
        isAppUser: userProfile.role === 'AppUser'
      };
    }
    return null;
  };

  const updateUserRole = async (email, newRole) => {
    const userProfileRef = collection(firestore, 'UserProfileMaster');
    const q = query(userProfileRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const docRef = doc(firestore, 'UserProfileMaster', querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        role: newRole,
        updatedAt: new Date().toISOString()
      });
      return true;
    }
    return false;
  };

  const SignInWithProvider = async (provider) => {
    try {
      if (!captchaVerified) {
        alert("Please verify CAPTCHA first.");
        return;
      }

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const email = user.email;

      // Check if this is a super admin based on environment variables
      const superAdminEmails = [
        process.env.REACT_APP_ADMIN_EMAIL1,
        process.env.REACT_APP_ADMIN_EMAIL2,
        process.env.REACT_APP_ADMIN_EMAIL3
      ].filter(Boolean); // Filter out any undefined values
      
      const isSuperAdminEnv = superAdminEmails.includes(email);

      // Get user profile from database
      let userProfile = await getUserProfile(email);

      // If user doesn't exist in database and is not a super admin, prevent login
      if (!userProfile && !isSuperAdminEnv) {
        alert("You are not authorized to use this application. Please contact an administrator.");
        return;
      }

      // If the user is a super admin but doesn't have a profile yet, create a basic super admin profile
      if (!userProfile && isSuperAdminEnv) {
        // Create a minimal profile for the super admin
        const userProfileRef = collection(firestore, 'UserProfileMaster');
        const uid = uuidv4();
        
        const superAdminProfile = {
          username: user.displayName || '',
          email,
          mobileNumber: null,
          uid,
          role: 'SuperAdmin', // Always SuperAdmin
          tenantId: 'super_admin', // Special tenant ID for super admins
          tenantName: 'Super Admin',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        await addDoc(userProfileRef, superAdminProfile);
        
        userProfile = {
          uid,
          role: 'SuperAdmin',
          tenantId: 'super_admin',
          tenantName: 'Super Admin',
          isSuperAdmin: true,
          isAppAdmin: false,
          isAppUser: false
        };
      }

      const { uid, role, tenantId, tenantName, isSuperAdmin, isAppAdmin, isAppUser } = userProfile;
                  
      const applicationId = "toJkt2xBHkBt";
      if(process.env.REACT_APP_ENV === 'production'){
        const requestBody = { 
          email, 
          applicationId,
          isAppAdmin,
          isAppUser,
          isSuperAdmin,
          role,
          tenantId
        };

        const response = await fetch('https://api-pilsilhmsa-uc.a.run.app/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (response.ok) {
          const jwtToken = data.token;
      
          Cookies.set('jwtToken', jwtToken, { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('userRole', role, { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('isAppAdmin', isAppAdmin.toString(), { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('isAppUser', isAppUser.toString(), { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('isSuperAdmin', isSuperAdmin.toString(), { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('userEmail', email, { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('tenantId', tenantId, { expires: 7, secure: true, sameSite: 'Strict' });
          Cookies.set('tenantName', tenantName, { expires: 7, secure: true, sameSite: 'Strict' });
      
          updateAuthState({
            isLoggedIn: true,
            isSuperAdmin,
            isAppAdmin,
            isAppUser,
            userEmail: email,
            tenantId,
            tenantName
          });
          navigate('/Home');
        } else {
          alert("Authentication failed: " + (data.message || "Unknown error. Please try again."));
        }
      } else {
        Cookies.set('jwtToken', 'mock-token', { path: '/' });
        Cookies.set('userRole', role, { path: '/' });
        Cookies.set('isAppAdmin', isAppAdmin.toString(), { path: '/' });
        Cookies.set('isAppUser', isAppUser.toString(), { path: '/' });
        Cookies.set('isSuperAdmin', isSuperAdmin.toString(), { path: '/' });
        Cookies.set('userEmail', email, { path: '/' });
        Cookies.set('tenantId', tenantId, { path: '/' });
        Cookies.set('tenantName', tenantName, { path: '/' });
        
        updateAuthState({
          isLoggedIn: true,
          isSuperAdmin,
          isAppAdmin,
          isAppUser,
          userEmail: email,
          tenantId,
          tenantName
        });
        navigate('/Home');
      }
    } catch (error) {
      alert("An error occurred during sign-in. Please try again.");
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  return (
    <div className="home-container">
      <h1>Welcome</h1>
      <div className="login-container">
        <div className="login-box">
          <div className="login-buttons">
            <Button 
              variant="contained" 
              startIcon={<GoogleIcon />} 
              onClick={() => SignInWithProvider(googleProvider)}
              style={{ margin: '8px 0', backgroundColor: '#4285F4', color: '#fff' }}
            >
              Login with Google
            </Button>
          </div>

          <div className="captcha-container">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_SITE_KEY}
              onChange={onCaptchaChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;